import { USD, int, magnitude } from '../lib/format';
import { C, ink, surface } from '../lib/theme';
import type { Filters, Reason } from '../lib/types';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useEventList } from '../lib/useEventList';
import { EventList } from './EventList';
import { EmptyState } from './primitives';
import type { EventCardHandlers } from './EventCard';
import type { Origin } from './ScaleModal';
import { IconArrowRight, IconCircleDashed, IconShieldCheck } from './icons';
import { useToast } from './Toast';

/**
 * Cartão lateral de Auditados — substitui a faixa "Abrir painel" (era um
 * banner de largura cheia). Agora vive ao lado da fila, sempre visível: o
 * operador vê o que já confirmou sem clicar em nada, e só abre o painel
 * completo (`ConfirmadosPanel`, com o waterfall e o resumo detalhado) quando
 * quer o detalhe. Retangular vertical de propósito — a coluna da fila (55%)
 * já é o elemento horizontal da tela, este é o par vertical.
 */
export function AuditadosCard({ filters, reasons, handlers, onOpenPanel }: {
  filters: Filters;
  reasons: Reason[];
  handlers: Omit<EventCardHandlers, 'onAudit' | 'onPickReason' | 'onSetCorrection' | 'onClearCorrection'>;
  onOpenPanel: (origin: Origin) => void;
}) {
  const toast = useToast();
  const kpis = useAsync(() => api.confirmadosKpis(filters), [JSON.stringify(filters)]);
  const list = useEventList(filters, 'confirmados', reasons);
  const k = kpis.data;
  const total = int(k?.total_eventos);

  const fullHandlers: EventCardHandlers = {
    ...handlers,
    onAudit: (ev) => list.toggleAudit(ev, (msg) => toast(msg, 'error')),
    onPickReason: (ev, picked) => list.setReason(ev, picked, (msg) => toast(msg, 'error')),
    onSetCorrection: (ev, body) => list.setCorrection(ev, body, (msg) => toast(msg, 'error')),
    onClearCorrection: (ev) => list.clearCorrection(ev, (msg) => toast(msg, 'error')),
  };

  return (
    <div style={{
      ...surface, display: 'flex', flexDirection: 'column',
      height: 'calc(100vh - 170px)', minHeight: 420, maxHeight: 760, overflow: 'hidden',
    }}>
      <div style={{ padding: '18px 18px 15px', borderBottom: `1px solid ${ink(0.07)}`, flex: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <span style={{
            width: 34, height: 34, borderRadius: 11, flex: 'none', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,144,112,0.16)', color: C.accent,
            border: '1px solid rgba(255,144,112,0.32)',
          }}><IconShieldCheck size={17} /></span>
          <div style={{ minWidth: 0, fontSize: 18.5, fontWeight: 800, letterSpacing: '-0.014em' }}>Auditados no período</div>
        </div>
        {/* Bruto (tudo que passou pela auditoria) ao lado do atualizado (depois
            de Anulado/VDI descontados) — pedido explícito de comparativo lado a
            lado, pra ficar óbvio que o card já desconta antes de mostrar o
            número final, sem precisar abrir o painel pra descobrir isso. */}
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, marginTop: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: ink(0.4), marginBottom: 4 }}>
              Auditado
            </div>
            <div style={{
              fontSize: 19, fontWeight: 800, letterSpacing: '-0.024em', fontVariantNumeric: 'tabular-nums', color: ink(0.78),
            }}>{kpis.loading ? '—' : USD(magnitude(k?.total_bruto))}</div>
            <div style={{ fontSize: 11, color: ink(0.42), marginTop: 2 }}>
              {kpis.loading ? 'carregando…' : `${total} evento${total === 1 ? '' : 's'} revisados`}
            </div>
          </div>
          <div style={{ width: 1, background: ink(0.08), flex: 'none' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.accent, marginBottom: 4 }}>
              Atualizado
            </div>
            <div style={{
              fontSize: 19, fontWeight: 800, letterSpacing: '-0.024em', fontVariantNumeric: 'tabular-nums',
              color: C.accent, textShadow: '0 0 24px rgba(255,144,112,0.28)',
            }}>{kpis.loading ? '—' : USD(magnitude(k?.perda_confirmada))}</div>
            <div style={{ fontSize: 11, color: ink(0.42), marginTop: 2 }}>
              {kpis.loading ? 'carregando…' : `sem anulado/VDI`}
            </div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '6px 8px' }}>
        <EventList
          items={list.items} loading={list.loading} loadingMore={list.loadingMore}
          error={list.error} hasMore={list.hasMore} busyIds={list.busyIds}
          variant="confirmados" reasons={reasons} filters={filters} handlers={fullHandlers}
          onLoadMore={list.loadMore} onRetry={list.reload}
          empty={
            <EmptyState
              tone="neutral" icon={<IconCircleDashed size={22} />} title="Nada confirmado ainda"
              text="Eventos aparecem aqui depois de auditados na fila."
            />
          }
        />
      </div>

      <div style={{ padding: 12, paddingTop: 8, flex: 'none' }}>
        {/* CTA com mais presença — pedido explícito de destacar o acesso
            rápido: gradiente igual ao botão primário de "Auditar" em vez do
            ghost neutro, já que este é o único jeito de chegar ao painel
            completo agora que a aba de topo saiu. */}
        <button
          type="button" onClick={(e) => onOpenPanel({ x: e.clientX, y: e.clientY })}
          className="ko-primary"
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
            border: 'none', background: `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`, color: '#fff',
            boxShadow: '0 8px 22px -4px rgba(232,85,48,0.55)',
            cursor: 'pointer', font: 'inherit', fontSize: 13, fontWeight: 750,
            padding: '12px 14px', borderRadius: 12,
          }}
        >
          Abrir painel completo<IconArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}
